import { Link, router } from '@inertiajs/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CurrentUser, VoteEdition } from '@/types';
import { googleSignInPath } from './google';

interface VoteHeaderProps {
  edition: VoteEdition;
  user: CurrentUser | null;
  hasGoogleAccount: boolean;
}

export function VoteHeader({
  edition,
  user,
  hasGoogleAccount,
}: VoteHeaderProps) {
  const handleLogin = () => {
    window.location.href = googleSignInPath(edition.votePath);
  };

  const handleSignOut = () => {
    router.delete('/auth/sign_out');
  };

  const profile =
    hasGoogleAccount && user ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-3 rounded-md transition-colors hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {user.image && (
              <img
                src={user.image}
                alt={user.name || 'User'}
                width={40}
                height={40}
                className="rounded-full border-2 border-primary"
              />
            )}
            <span
              className={`hidden text-primary sm:block ${
                edition.variant === '25' ? 'font-title' : 'font-sans'
              }`}
            >
              {user.name}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 border-primary/20 bg-background"
        >
          <div className="px-2 py-1.5">
            <p
              className={`text-primary text-sm ${
                edition.variant === '25' ? 'font-title' : 'font-sans'
              }`}
            >
              {user.email}
            </p>
          </div>
          <DropdownMenuSeparator className="bg-primary/20" />
          <DropdownMenuItem
            onClick={handleSignOut}
            className={`cursor-pointer text-primary focus:bg-primary/5 focus:text-primary ${
              edition.variant === '25' ? 'font-title' : 'font-sans'
            }`}
          >
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <button
        type="button"
        onClick={handleLogin}
        className={
          edition.variant === '25'
            ? 'border-2 border-primary bg-transparent px-4 py-2 font-bold font-title text-primary transition-all hover:bg-primary hover:text-background sm:px-6 sm:py-3'
            : 'border-2 border-primary bg-transparent px-4 py-2 font-sans text-primary transition-all hover:bg-primary hover:text-background sm:px-6 sm:py-3'
        }
      >
        Login
      </button>
    );

  return (
    <div className="border-primary/20 border-b bg-background">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
        {edition.variant === '25' ? (
          <Link href="/25/vote">
            <h1 className="flex items-center gap-2 bg-primary px-2 py-1 text-2xl transition-opacity hover:opacity-80 sm:gap-3 sm:px-3 sm:text-5xl md:text-6xl">
              <span className="font-logo text-background lowercase tracking-tighter">
                <span className="font-light">platanus hack</span>{' '}
                <span className="font-medium">[25]</span>
              </span>
              <span className="text-background">|</span>
              <span className="font-bold font-logo text-background lowercase tracking-tighter">
                voting
              </span>
            </h1>
          </Link>
        ) : (
          <Link
            href={edition.votePath}
            className="inline-block transition-opacity hover:opacity-80"
          >
            <h1 className="flex items-center gap-2 text-2xl sm:gap-3 sm:text-5xl md:text-6xl">
              <span className="font-logo text-primary lowercase tracking-tighter">
                <span className="font-light">platanus hack</span>{' '}
                <span className="font-medium">[26]</span>
              </span>
              <span className="text-primary">|</span>
              <span className="bg-primary px-2 font-logo text-background lowercase tracking-tighter">
                voting
              </span>
            </h1>
            <p
              className="font-sans text-primary text-xs uppercase sm:text-sm md:text-base"
              style={{ letterSpacing: '0.4em' }}
            >
              {edition.city}
            </p>
          </Link>
        )}

        {/* Login/Profile Section */}
        <div className="flex items-center">{profile}</div>
      </div>
    </div>
  );
}
